/**
 * S2N-3173: the client half of ParLevelHelper.cs — the one set of PAR rules the legacy tables use.
 *
 * Limits are nullable. Null means that bound is not set (no floor / no ceiling) and is shown as
 * "N/A"; 0 is a real limit of zero, so a maximum of 0 with stock on hand is ABOVE. The retired -1
 * sentinel (or any negative) is treated as not set, so a stale value cannot become a target.
 *
 * Training and quarantine inventories never reach these checks with limits: the server sends them
 * null, so no PAR block renders for them.
 */
export class ParLevelHelper {
    static readonly NotSetLabel = 'N/A';

    static isSet(limit: number | null | undefined): limit is number {
        return limit !== null
            && limit !== undefined
            && limit >= 0;
    }

    /** Either bound configured — the legacy "show a PAR block" test. */
    static hasPar(
        minimum: number | null | undefined
        , maximum: number | null | undefined
    ): boolean { return ParLevelHelper.isSet(minimum) || ParLevelHelper.isSet(maximum); }

    /** Stock on hand under a configured floor. Zero stock is OUT, not BELOW. */
    static isBelow(
        currentStock: number | null | undefined
        , minimum: number | null | undefined
    ): boolean {
        const stock = currentStock ?? 0;
        return stock > 0
            && ParLevelHelper.isSet(minimum)
            && stock < minimum;
    }

    /** Stock over a configured ceiling. A null maximum is no ceiling. */
    static isAbove(
        currentStock: number | null | undefined
        , maximum: number | null | undefined
    ): boolean {
        const stock = currentStock ?? 0;
        return stock > 0
            && ParLevelHelper.isSet(maximum)
            && stock > maximum;
    }

    /** None on hand while a positive floor is configured. */
    static isOut(
        currentStock: number | null | undefined
        , minimum: number | null | undefined
    ): boolean {
        return (currentStock ?? 0) <= 0
            && ParLevelHelper.isSet(minimum)
            && minimum > 0;
    }

    /** A bound for display: the number with separators, or "N/A" when not set. */
    static format(limit: number | null | undefined): string {
        return ParLevelHelper.isSet(limit)
            ? limit.toLocaleString('en-US', { maximumFractionDigits: 3 })
            : ParLevelHelper.NotSetLabel;
    }

    /** "min | max unit", each side "N/A" when not set. */
    static formatRange(
        minimum: number | null | undefined
        , maximum: number | null | undefined
        , unitType: string | null | undefined
    ): string {
        return `${ParLevelHelper.format(minimum)} | ${ParLevelHelper.format(maximum)} ${unitType ?? ''}`.trim();
    }
}
